import { useDispatch } from 'app-redux'
import { Config } from 'common/config'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
	ProjectorMessage,
	readConsoleMessage,
} from './model/presenter-messages'
import { bridgeChannelName } from './model/presenter-url'
import { openBroadcastChannel } from './replication/channel'
import { remoteStateReceived } from './replication/remote-state'
import { Replica, startReplica } from './replication/replica'
import { webLocks } from './window-management'

export type ProjectorLink = {
	/** The first snapshot has arrived. */
	synced: boolean
	/** The console is there (it may be reloading). */
	consolePresent: boolean
	/** The console runs another build; both windows must be reloaded. */
	otherBuild: boolean
	post: (message: ProjectorMessage) => void
}

/**
 * The presentation window's end of the link: the console's state goes into
 * this window's store, and `onSnapshot` runs with every snapshot - also the
 * first one from a console that reloaded. `onReloadMedia` runs when the
 * console asks the recording's player to load its file again. The window
 * closes itself when the console ends the presentation.
 */
export const useProjectorLink = (
	liveSessionId: string,
	presentation: string,
	onSnapshot: () => void,
	onReloadMedia: () => void
): ProjectorLink => {
	const dispatch = useDispatch()
	const [synced, setSynced] = useState(false)
	const [consolePresent, setConsolePresent] = useState(false)
	const [otherBuild, setOtherBuild] = useState(false)
	const replica = useRef<Replica | null>(null)
	const handlers = useRef({ onSnapshot, onReloadMedia })
	handlers.current = { onSnapshot, onReloadMedia }

	useEffect(() => {
		const channel = openBroadcastChannel(
			bridgeChannelName(liveSessionId, presentation)
		)
		replica.current = startReplica({
			channel,
			address: { link: presentation, build: Config.VERSION },
			locks: webLocks(),
			applySlices: (slices, kind) => {
				dispatch(remoteStateReceived({ slices }))
				if (kind === 'snapshot') {
					setSynced(true)
					handlers.current.onSnapshot()
				}
			},
			// Relayed as the console's store applied them: plain actions.
			applyActions: actions => actions.forEach(action => dispatch(action)),
			onMessage: payload => {
				switch (readConsoleMessage(payload)?.kind) {
					case 'end':
						window.close()
						return
					case 'reload-media':
						handlers.current.onReloadMedia()
						return
				}
			},
			onPeer: setConsolePresent,
			onOtherBuild: () => setOtherBuild(true),
		})
		return () => {
			replica.current?.stop()
			replica.current = null
			channel.close()
		}
	}, [dispatch, liveSessionId, presentation])

	const post = useCallback(
		(message: ProjectorMessage) => replica.current?.post(message),
		[]
	)

	return { synced, consolePresent, otherBuild, post }
}
